import React, { useMemo, useState, useCallback, useReducer, useEffect } from "react";

import { useDebouncedCallback } from "use-debounce";

import { InlineEditPopup } from "./InlineEditPopup";

export interface IOpenEditPopupOptions {
  elementId: string;
  onValueChanged: (value?: string) => void;
  initialValue?: string;
  originalValue?: string;
  popupClassName?: string;
}

export interface IInlineEditContext {
  onEditPopupOpened: (options: IOpenEditPopupOptions) => void;
  popupElement?: React.ReactElement;
  currentElementId?: string;
  onEditPopupClosed: () => void;
}

enum EditState {
  View,
  Edit,
  PendingEdit,
  Saving
}

interface IInlineEditProviderState {
  currentElementId?: string;
  onValueChanged?: (value?: string) => void;
  initialValue?: string;
  originalValue?: string;
  popupClassName?: string;
  state: EditState;
  pendingOptions?: IOpenEditPopupOptions;
}

type Action = { type: "start-edit"; options: IOpenEditPopupOptions } | { type: "open-edit" } | { type: "close-edit" } | { type: "save" };

/* istanbul ignore next */
export const InlineEditContext = React.createContext<IInlineEditContext>({
  onEditPopupOpened: () => {},
  onEditPopupClosed: () => {}
});

const createReducer = () => {
  return (state: IInlineEditProviderState, action: Action): IInlineEditProviderState => {
    switch (action.type) {
      case "start-edit": {
        if (state.currentElementId !== action.options.elementId) {
          return { ...state, state: EditState.PendingEdit, pendingOptions: action.options };
        }

        return state;
      }
      case "save": {
        return { ...state, state: EditState.Saving };
      }
      case "open-edit": {
        if (state.pendingOptions) {
          const opt = state.pendingOptions;
          return {
            state: EditState.Edit,
            onValueChanged: opt.onValueChanged,
            currentElementId: opt.elementId,
            initialValue: opt.initialValue,
            originalValue: opt.originalValue,
            popupClassName: opt.popupClassName
          };
        }
        return { state: EditState.View, initialValue: state.initialValue, originalValue: state.originalValue, popupClassName: state.popupClassName };
      }
      case "close-edit": {
        return { state: EditState.View, initialValue: state.initialValue, originalValue: state.originalValue, popupClassName: state.popupClassName };
      }
    }

    /* istanbul ignore next */
    return state;
  };
};

export const InlineEditProvider: React.FC = ({ children }) => {
  const [{ currentElementId, onValueChanged, initialValue, popupClassName, originalValue, state }, dispatch] = useReducer(createReducer(), { state: EditState.View });
  const [currentValue, setCurrentValue] = useState<string>();

  const closeEditPopup = useCallback(() => {
    dispatch({ type: "close-edit" });
  }, []);

  const saveValue = useCallback(() => {
    dispatch({ type: "save" });
  }, []);

  const popupElement = useMemo(() => {
    return (
      <InlineEditPopup
        onValueChanged={setCurrentValue}
        originalValue={originalValue}
        initialValue={initialValue}
        className={popupClassName}
        onEditPopupClosed={closeEditPopup}
        onValueCommited={saveValue}
      />
    );
  }, [initialValue, popupClassName, closeEditPopup, originalValue, saveValue]);

  useEffect(() => {
    switch (state) {
      case EditState.PendingEdit: {
        if (onValueChanged) {
          dispatch({ type: "save" });
        } else {
          dispatch({ type: "open-edit" });
        }
        break;
      }
      case EditState.Saving: {
        if (onValueChanged && (initialValue || "") !== currentValue) {
          onValueChanged(currentValue);
        }
        dispatch({ type: "open-edit" });
        break;
      }
    }
  }, [onValueChanged, initialValue, currentValue, state]);

  const [openEditPopup] = useDebouncedCallback(
    (options: IOpenEditPopupOptions) => {
      dispatch({ type: "start-edit", options: options });
    },
    50,
    { leading: true }
  );

  const context = useMemo<IInlineEditContext>(() => {
    return {
      onEditPopupOpened: openEditPopup,
      currentElementId: state === EditState.Edit ? currentElementId : undefined,
      popupElement: popupElement,
      onEditPopupClosed: closeEditPopup
    };
  }, [popupElement, state, currentElementId, openEditPopup, closeEditPopup]);

  return <InlineEditContext.Provider value={context}>{children}</InlineEditContext.Provider>;
};
